import type { CoachingSession } from '@efdigital/ll-addon-data';

const CANCELLABLE_STATUSES = ['scheduled', 'awaiting_coach_confirmation'];

export type SessionAdminCapabilities = {
  canResolve: boolean;
  canForceAbandon: boolean;
  canGrantQuota: boolean;
  canCancel: boolean;
  canReschedule: boolean;
  canClearExternalReview: boolean;
};

export function getSessionAdminCapabilities(
  session: CoachingSession,
  canEdit: boolean
): SessionAdminCapabilities {
  return {
    canResolve: canEdit && session.status === 'awaiting_attendance_review',
    canForceAbandon: canEdit && session.status === 'booking_pending',
    canGrantQuota: canEdit,
    canCancel: canEdit && CANCELLABLE_STATUSES.includes(session.status),
    canReschedule: canEdit && session.status === 'scheduled',
    canClearExternalReview:
      canEdit && session.status === 'external_change_flagged',
  };
}

/** Matches ll-cdk-coaching booking pending TTL (30 minutes). */
export const BOOKING_PENDING_TTL_MS = 30 * 60 * 1000;

export function isStaleBookingPending(session: CoachingSession): boolean {
  if (session.status !== 'booking_pending' || !session.bookingPendingAt) {
    return false;
  }

  return (
    Date.now() - new Date(session.bookingPendingAt).getTime() >
    BOOKING_PENDING_TTL_MS
  );
}

function coachConfirmationExpiresAt(
  session: CoachingSession
): string | null | undefined {
  return (
    session as CoachingSession & {
      coachConfirmationExpiresAt?: string | null;
    }
  ).coachConfirmationExpiresAt;
}

/** True when a session is still awaiting coach confirmation past its expiry time. */
export function isExpiredCoachConfirmation(session: CoachingSession): boolean {
  if ((session.status as string) !== 'awaiting_coach_confirmation') {
    return false;
  }

  const expiresAt = coachConfirmationExpiresAt(session);
  if (!expiresAt) {
    return false;
  }

  const expiresAtMs = Date.parse(expiresAt);
  if (!Number.isFinite(expiresAtMs)) {
    return false;
  }

  return Date.now() >= expiresAtMs;
}
